import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useToast } from '@sketch/toasts'
import { capitalizeFirst } from '@sketch/utils'

const DEFAULT_SUCCESS_MESSAGE = 'Sign in method added'
const DEFAULT_ERROR_MESSAGE = 'Failed to add sign in method, please try again'

const SOCIAL_SETTINGS_MESSAGES: Record<
  string,
  { success: string; error: string }
> = {
  linkAccount: {
    success: ':provider sign in method added',
    error: 'Failed to add :provider sign in method, please try again',
  },
}

/**
 * Handles social account linking results in the settings page.
 *
 * During the social account linking flow, after the user completes the process,
 * they are redirected back to the settings page with result details in the URL parameters.
 * This hook checks for these parameters, displays an appropriate toast notification,
 * and then cleans up the URL to remove the parameters.
 *
 * Expected URL parameters:
 * - success: 'true' or 'false' indicating if the operation was successful
 * - action: 'linkAccount' indicating the type of operation performed
 * - authp: The authentication provider (e.g., 'google', 'apple')
 *
 */
export const useSocialSettingsErrorHandler = () => {
  const { showToast } = useToast()
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const successParam = urlParams.get('success')
    const actionParam = urlParams.get('action')
    const provider = urlParams.get('authp')

    if (successParam !== null && actionParam) {
      const isSuccess = successParam === 'true'
      const toastType = isSuccess ? 'positive' : 'negative'

      const messageTemplate = SOCIAL_SETTINGS_MESSAGES[actionParam]

      if (messageTemplate) {
        const baseMessage = isSuccess
          ? messageTemplate.success
          : messageTemplate.error

        // Replace provider placeholder if present
        if (baseMessage.includes(':provider') && provider) {
          const formattedMessage = baseMessage.replace(
            ':provider',
            capitalizeFirst(provider)
          )
          showToast(formattedMessage, toastType)
        } else {
          showToast(baseMessage, toastType)
        }
      } else {
        // Fallback to default messages for unknown actions
        const fallbackMessage = isSuccess
          ? DEFAULT_SUCCESS_MESSAGE
          : DEFAULT_ERROR_MESSAGE
        showToast(fallbackMessage, toastType)
      }

      // Clear all URL parameters
      history.replace(location.pathname)
    }
  }, [location.search, location.pathname, showToast, history])
}
